const MIN = 8;
const MAX_BYTES = 72;

// Exported so the interpolated messages in auth.json can name the same numbers
// this file enforces, instead of hardcoding "8" in five translations that would
// then silently disagree with the code the day MIN changes.
export const PASSWORD_LIMITS = { min: MIN, maxBytes: MAX_BYTES };

// These used to be English sentences returned straight from validatePassword and
// rendered as-is, which put "8+ characters, with at least three of: lowercase,
// uppercase, digit, symbol." under a field labelled "Mot de passe". The rules
// live here; the words live in the locale files.
//
// Namespace-qualified so the same return value works from the auth pages (whose
// default namespace is `auth`) and from the dashboard's account settings (whose
// default is `dashboard`).
export const PASSWORD_HINT_KEY = "auth:password.hint";

const ERROR_KEYS = {
  TOO_SHORT: "auth:password.tooShort",
  TOO_LONG: "auth:password.tooLong",
  WHITESPACE: "auth:password.whitespace",
  WEAK: "auth:password.weak",
};

// UTF-8 byte length, counted by hand.
//
// This was `new TextEncoder().encode(v).length`, which jsdom does not provide —
// so the byte rule and everything after it had never once executed under test.
// The obvious replacement, `encodeURIComponent(...)`, throws URIError on a lone
// surrogate, and that throw escapes every submit handler: the button just dies,
// no message, no spinner. You cannot type a lone surrogate but you can paste
// one, from a generator or a mangled clipboard.
//
// So: explicit, total, and matching TextEncoder exactly — including its handling
// of an unpaired surrogate, which it replaces with U+FFFD and therefore counts
// as three bytes.
function utf8Bytes(v) {
  let bytes = 0;
  for (let i = 0; i < v.length; i += 1) {
    const code = v.charCodeAt(i);
    if (code < 0x80) {
      bytes += 1;
    } else if (code < 0x800) {
      bytes += 2;
    } else if (code >= 0xd800 && code <= 0xdbff) {
      const low = v.charCodeAt(i + 1);
      if (low >= 0xdc00 && low <= 0xdfff) {
        bytes += 4; // a real astral character, encoded from the pair
        i += 1;
      } else {
        bytes += 3; // high surrogate with nothing after it -> U+FFFD
      }
    } else if (code >= 0xdc00 && code <= 0xdfff) {
      bytes += 3; // low surrogate with nothing before it -> U+FFFD
    } else {
      bytes += 3;
    }
  }
  return bytes;
}

// Returns null when the password is acceptable, otherwise the i18n key of the
// reason. Callers translate: t(key, PASSWORD_LIMITS).
export function validatePassword(v) {
  if (typeof v !== "string" || v.length < MIN) {
    return ERROR_KEYS.TOO_SHORT;
  }
  // bcrypt silently truncates past 72 bytes, so a longer password would be
  // accepted here and then only partly checked at login.
  if (utf8Bytes(v) > MAX_BYTES) {
    return ERROR_KEYS.TOO_LONG;
  }
  if (v.trim() !== v) {
    return ERROR_KEYS.WHITESPACE;
  }
  const classes = [/\p{Ll}/u, /\p{Lu}/u, /\d/, /[^\p{L}\p{N}]/u].reduce(
    (n, re) => n + (re.test(v) ? 1 : 0),
    0,
  );
  if (classes < 3) {
    return ERROR_KEYS.WEAK;
  }
  return null;
}

// Confirmation is only worth its friction at the two moments a password is
// *created* — signup and reset. A typo there is invisible: the account is made
// with a value its owner never knew, and every later login says "invalid
// password" about a password they think they remember. That is a real user in
// production: 54 clicks on /login, then a reset.
//
// Returned before any strength check so an obvious mismatch is not buried under
// a lecture about symbols. An empty confirm field returns null — nothing to
// disagree with yet, and `required` already blocks the submit.
export function confirmError(password, confirm) {
  if (!confirm) return null;
  return password === confirm ? null : "MISMATCH";
}
